import c from 'classnames';
import React from 'react';

import { Button, ButtonColors, TextColor, Typography } from '@repo/ui';
import {
  AgeGroup,
  downloadIcsEvent,
  formatRetestMonth,
  getAgeGroup,
  getRetestTiming,
  getTrimester,
  type RetestTiming,
} from '@repo/utils';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useCheckoutKit } from 'hooks/useCheckoutKit';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import useSubscriptions from 'services/general/subscriptionsList';
import { MainFinding } from 'views/Results/Summary/SummaryCards/NoteCard/useMainFindings';
import { SLIDE_BACKGROUND_COLORS } from '../slideBackgroundColors';
import { RetestChart } from './RetestChart';
import { RetestPriorityCard } from './RetestPriorityCard';
import styles from './RetestPrompt.module.css';
import slideStyles from './Slides.module.css';

const UPSELL_UTM =
  'utm_source=tinyhealth&utm_medium=webapp&utm_campaign=first-look-retest';

/**
 * Ordered because the two header lines swap: normally the date is the large
 * line below its label, but once the retest date has passed the call to action
 * becomes the large line and moves above the date sentence.
 */
type HeaderLine = { text: string; emphasis: 'lead' | 'supporting' };

const getHeaderLines = ({
  timing,
  hasPriorities,
  formattedDate,
  leadText,
}: {
  timing: RetestTiming | null;
  hasPriorities: boolean;
  /** The retest month, used verbatim in the overdue sentence. */
  formattedDate: string | null;
  /** What the large line says — the month, or a trimester when pregnant. */
  leadText: string | null;
}): HeaderLine[] => {
  if (timing === 'overdue') {
    return [
      { text: "It's time to retest!", emphasis: 'lead' },
      ...(formattedDate
        ? [
            {
              text: `We recommended retesting in ${formattedDate}.`,
              emphasis: 'supporting' as const,
            },
          ]
        : []),
    ];
  }

  return [
    {
      text: hasPriorities
        ? 'Recommended retesting date:'
        : 'Retest to monitor changes in:',
      emphasis: 'supporting',
    },
    ...(leadText ? [{ text: leadText, emphasis: 'lead' as const }] : []),
  ];
};

interface RetestPromptProps {
  topThreeMetrics: MainFinding[];
  resampleMonth: string;
}

export const RetestPrompt: React.FC<RetestPromptProps> = ({
  topThreeMetrics,
  resampleMonth,
}) => {
  const { trackEvent } = useAnalytics();
  const { currentKit, currentTinyAccount } = useCurrentKitAndTinyAccount();
  const { config: portalConfig } = usePortalConfigs(currentKit);
  const { isAnyTinyPlus } = useSubscriptions();
  const { checkoutUrl } = useCheckoutKit({
    kitType: currentKit?.sampling_type,
  });

  const hasPriorities = topThreeMetrics.length > 0;
  const ageGroup = getAgeGroup(currentKit?.age_at_sampling ?? 0);
  const isAdult = ageGroup === AgeGroup.ADULT;

  const isPregnant = Boolean(currentTinyAccount?.expected_due_date);
  // TODO(ENG-4760): currentKit.ttc (trying to conceive, not yet pregnant) has
  // its own copy variants in Figma that aren't wired up yet.
  const trimester =
    isPregnant && currentTinyAccount?.expected_due_date
      ? getTrimester(new Date(currentTinyAccount.expected_due_date))
      : null;

  // Displayed as "[Month Year]", but the day-level value drives the CTA
  // bucketing, so this must be a parseable date rather than a month label.
  const retestDate = resampleMonth ? new Date(resampleMonth) : null;
  const timing = retestDate ? getRetestTiming(retestDate) : null;

  const subjectPossessive = isAdult
    ? 'your'
    : `${currentTinyAccount?.first_name}'s`;

  const formattedDate = retestDate ? formatRetestMonth(retestDate) : null;

  // Pregnancy variants headline the trimester rather than a calendar month.
  // Postpartum has no trimester to name, so it falls back to the date.
  const trimesterLabel =
    trimester && trimester !== 'postpartum' ? `${trimester} trimester` : null;

  const headerLines = getHeaderLines({
    timing,
    hasPriorities,
    formattedDate,
    leadText: trimesterLabel ?? formattedDate,
  });

  // The chart variant is header + illustration only, per Figma.
  const description = hasPriorities
    ? `See if ${subjectPossessive} top priorities change:`
    : null;

  // Buying a kit is an upsell-adjacent purchase action; a calendar reminder
  // is not, so hide_upsells only ever suppresses the former.
  const showBuyKitCta =
    (timing === 'overdue' || timing === 'within-a-month') &&
    !portalConfig.hide_upsells &&
    !isAnyTinyPlus;

  const showCreateReminderCta = timing === 'more-than-a-month';

  const showUpsellLine = !portalConfig.hide_upsells && !isAnyTinyPlus;

  const upsellCopy =
    timing === 'more-than-a-month'
      ? 'Plan on retesting regularly? Upgrade to Tiny+ to enjoy discounts on kits.'
      : 'Upgrade to Tiny+ to save on retesting.';

  const handleBuyKit = () => {
    trackEvent('First Look', {
      event_type: 'button_clicked',
      button_text: 'Buy a kit',
      slide_id: 'retest-prompt',
      action: 'buy_kit',
    });
    window.open(`${checkoutUrl}&${UPSELL_UTM}`, '_blank');
  };

  const handleCreateReminder = () => {
    trackEvent('First Look', {
      event_type: 'button_clicked',
      button_text: 'Create reminder',
      slide_id: 'retest-prompt',
      action: 'create_reminder',
    });
    if (!retestDate) return;
    downloadIcsEvent({
      title: 'Time to retest with Tiny Health',
      description: `${isAdult ? 'Your' : subjectPossessive} recommended retest date has arrived.`,
      date: retestDate,
    });
  };

  return (
    <div
      className={c(slideStyles.slideWrapper, styles.slideContainer)}
      style={{ backgroundColor: SLIDE_BACKGROUND_COLORS.retestPrompt }}
    >
      <div className={styles.contentContainer}>
        <div className={styles.header}>
          {headerLines.map(line =>
            line.emphasis === 'lead' ? (
              <Typography
                key={line.text}
                variant='heading-2xl'
                color={TextColor.DARKPURPLE}
                weight='medium'
              >
                {line.text}
              </Typography>
            ) : (
              <Typography key={line.text} variant='body-m'>
                {line.text}
              </Typography>
            ),
          )}

          {showUpsellLine && (
            <Typography variant='body-s' color='grey'>
              {upsellCopy}
            </Typography>
          )}
        </div>

        <div className={styles.body}>
          {description && (
            <Typography variant='body-m'>{description}</Typography>
          )}

          {hasPriorities ? (
            <div className={styles.priorityList}>
              {topThreeMetrics.map((metric, index) => (
                <RetestPriorityCard
                  key={metric.sample_health_id ?? index}
                  metric={metric}
                />
              ))}
            </div>
          ) : (
            <RetestChart />
          )}
        </div>

        <div className={styles.buttonsContainer}>
          {showBuyKitCta && (
            <Button
              variant='outline'
              color={ButtonColors.PURPLE}
              onClick={handleBuyKit}
              label='Buy a kit'
              iconNameLeft='shoppingCart'
              width='full'
            />
          )}
          {showCreateReminderCta && (
            <Button
              variant='outline'
              color={ButtonColors.PURPLE}
              onClick={handleCreateReminder}
              label='Create reminder'
              iconNameLeft='calendar'
              width='full'
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RetestPrompt;
